import {
	Container,
	defaultValue,
	extractAttributesFromGlProgram,
	getUboData,
	getUniformData,
	GlProgram,
	GlProgramData,
	Matrix,
	Mesh,
	RenderTexture,
	Shader,
	Texture,
	UniformGroup,
} from 'pixi.js'
import { whenAppLoaded } from '~/lib/appLoaded'
import tempo from '~/lib/clock/tempo'
import { decodePick, pickFragment } from '~/lib/drag/pick'
import { getObjectInitial, sourceValues, SPEED_SYNC } from '~/lib/params'
import { refKey } from '~/lib/outline'
import { timed } from '~/lib/stageTiming'
import { scaffoldVariant } from '~/lib/shaders/common'
import {
	advanceTrail,
	boundParams,
	buildFragment,
	frameUniforms,
	getShader,
	heldParamsOf,
	heldUniforms,
	integratedParamsOf,
	liveRateUniform,
	lockUniforms,
	perUnit,
	rateBaseOf,
	rateUniformOf,
	SHADER_PREFIX,
	stageUnit,
	steadyTrail,
	SURFACE_VERTEX,
	trailOf,
	trailUniform,
	TRAIL_AT,
	turnsUniform,
	uniformName,
	uniformValue,
	variantOf,
	wrapCycle,
} from '~/lib/shaders'
import { alongScale, coastTarget, depthUniform, fieldLead } from '~/lib/shaders/common'
import { newSea, seaUniformDecls, stepSea, uploadSea } from '~/lib/shaders/pipeline/sea'
import { HEIGHT, WIDTH } from '~/lib/constants'
import { sourceAspect } from '~/lib/sources/textures'
import { nodeQuad, nodeRefs } from '~/lib/surfaceFrames'
import {
	buildRectDraws,
	buildSurfaceDraws,
	fieldRect,
	frameUniform,
	quadGeometry,
	refillQuad,
	surfaceGeometryKey,
	toEnv,
} from './shaderSurfaces'
import {
	acquireFieldTexture,
	bindFieldTextures,
	chosenFieldFilter,
	fieldFilterOf,
	releaseFieldTexture,
	releaseFieldTextures,
} from './fieldTextures'

const logger = console.scope('PixiShaderMesh')

// One compiled program per build (`<variant key>|<field filter>`, `|pick<axis>` for pick builds), shared across instances and rebuilds.
const programs = new Map()

// Link results per shader build: true | an error string. Pixi compiles lazily at FIRST RENDER — a bad shader must skip its object here, or stay dark until linked (`awaitLink`), never throw inside the render loop.
const compileResults = new Map()

// The field filter ('linear' | 'manual') is fieldTextures.js's, so the warm uploads what a node will bind.
/** The field filter the stage chose, or null before the first shader mesh — what the probe reports (U4e). */
export const shaderFieldFilter = () => chosenFieldFilter()

// Links both stages too — a fragment that compiles can still fail the link. `variant` is `variantOf`'s: a standalone shader's name, a subset's pipeline build. ⚠️ Blocks for the whole link: only `switchShaderVariant` onto another build (scaffold) reaches it uncached.
const compileCheck = (variant, sharedGl, filter = fieldFilterOf()) => {
	const key = `${variant.key}|${filter}`
	const cached = compileResults.get(key)
	if (cached !== undefined) return cached

	let fragment
	try {
		fragment = buildFragment(variant, filter)
	} catch (e) {
		compileResults.set(key, e.message)
		return e.message
	}

	let result = true
	// Only a verdict from a live context is cached; without one the shader goes to Pixi unchecked.
	let checked = false
	let gl = sharedGl
	try {
		gl = gl || new OffscreenCanvas(1, 1).getContext('webgl2')
		if (gl && !gl.isContextLost()) {
			const stage = (type, src) => {
				const s = gl.createShader(type)
				gl.shaderSource(s, src)
				gl.compileShader(s)
				return s
			}
			const vs = stage(gl.VERTEX_SHADER, SURFACE_VERTEX)
			const fs = stage(gl.FRAGMENT_SHADER, fragment)
			if (!timed('check', () => gl.getShaderParameter(fs, gl.COMPILE_STATUS))) {
				result = gl.getShaderInfoLog(fs) || 'unknown compile error'
			} else {
				const prog = gl.createProgram()
				gl.attachShader(prog, vs)
				gl.attachShader(prog, fs)
				gl.linkProgram(prog)
				if (!timed('check', () => gl.getProgramParameter(prog, gl.LINK_STATUS)))
					result = gl.getProgramInfoLog(prog) || 'link failed'
				gl.deleteProgram(prog)
			}
			gl.deleteShader(vs)
			gl.deleteShader(fs)
			// A context lost mid-check reads every status as a failure.
			checked = !gl.isContextLost()
		}
	} catch (e) {
		logger.warn('compile check unavailable', e)
	} finally {
		// An owned probe context counts against the browser's context cap beside Pixi's; a shared one is the caller's to release.
		if (!sharedGl) gl?.getExtension('WEBGL_lose_context')?.loseContext()
	}
	if (!checked) return true
	compileResults.set(key, result)
	return result
}

/** `compileCheck`'s verdict without its blocking read: the probe context links through KHR_parallel_shader_compile and the status is read once complete. */
const probeLink = (variant, filter) => {
	const key = `${variant.key}|${filter}`
	const cached = compileResults.get(key)
	if (cached !== undefined) return Promise.resolve(cached)
	let fragment
	try {
		fragment = buildFragment(variant, filter)
	} catch (e) {
		compileResults.set(key, e.message)
		return Promise.resolve(e.message)
	}
	return new Promise(resolve => {
		let gl = null
		try {
			gl = new OffscreenCanvas(1, 1).getContext('webgl2')
		} catch (e) {
			logger.warn('compile check unavailable', e)
		}
		if (!gl || gl.isContextLost()) return resolve(true)
		const ext = gl.getExtension('KHR_parallel_shader_compile')
		const stage = (type, src) => {
			const s = gl.createShader(type)
			gl.shaderSource(s, src)
			gl.compileShader(s)
			return s
		}
		const vs = stage(gl.VERTEX_SHADER, SURFACE_VERTEX)
		const fs = stage(gl.FRAGMENT_SHADER, fragment)
		const prog = gl.createProgram()
		gl.attachShader(prog, vs)
		gl.attachShader(prog, fs)
		gl.linkProgram(prog)
		const finish = () => {
			let result = true
			if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS))
				result = gl.getShaderInfoLog(fs) || 'unknown compile error'
			else if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) result = gl.getProgramInfoLog(prog) || 'link failed'
			const checked = !gl.isContextLost()
			gl.getExtension('WEBGL_lose_context')?.loseContext()
			if (!checked) return resolve(true)
			compileResults.set(key, result)
			resolve(result)
		}
		// Timers, not rAF: a hidden tab never fires it.
		const poll = () => {
			if (gl.isContextLost()) return resolve(true)
			if (ext && !gl.getProgramParameter(prog, ext.COMPLETION_STATUS_KHR)) return setTimeout(poll, 16)
			finish()
		}
		poll()
	})
}

const VERSION_300 = /^[ \t]*#[ \t]*version[ \t]+300[ \t]+es[ \t]*$/m

/** Links `program` on the stage's context through KHR_parallel_shader_compile and seeds Pixi's program cache with it, as Pixi's `generateProgram` would: its first draw then never waits on a link (a union waited 2–2.4 s). Resolves the verdict (true | log), or null when the context cannot link off the main thread. */
const linkOffThread = (renderer, program) =>
	new Promise(resolve => {
		const gl = renderer?.gl
		const cache = renderer?.shader?._programDataHash
		const ext = gl?.getExtension('KHR_parallel_shader_compile')
		if (!ext || !cache || gl.isContextLost()) return resolve(null)
		if (cache[program._key]) return resolve(true)
		const stage = (type, src) => {
			const s = gl.createShader(type)
			gl.shaderSource(s, src)
			gl.compileShader(s)
			return s
		}
		const vs = stage(gl.VERTEX_SHADER, program.vertex)
		const fs = stage(gl.FRAGMENT_SHADER, program.fragment)
		const prog = gl.createProgram()
		gl.attachShader(prog, vs)
		gl.attachShader(prog, fs)
		gl.linkProgram(prog)
		const poll = () => {
			const hash = renderer.shader?._programDataHash
			if (gl.isContextLost() || !hash) return resolve(null)
			if (!gl.getProgramParameter(prog, ext.COMPLETION_STATUS_KHR)) return requestAnimationFrame(poll)
			let result = true
			if (!gl.getProgramParameter(prog, gl.LINK_STATUS))
				result = gl.getShaderInfoLog(fs) || gl.getProgramInfoLog(prog) || 'link failed'
			else if (!hash[program._key]) {
				program._attributeData = extractAttributesFromGlProgram(prog, gl, !VERSION_300.test(program.vertex))
				program._uniformData = getUniformData(prog, gl)
				program._uniformBlockData = getUboData(prog, gl)
				const uniforms = {}
				for (const name in program._uniformData) {
					const { type, size } = program._uniformData[name]
					uniforms[name] = { location: gl.getUniformLocation(prog, name), value: defaultValue(type, size) }
				}
				hash[program._key] = new GlProgramData(prog, uniforms)
			}
			gl.deleteShader(vs)
			gl.deleteShader(fs)
			if (hash[program._key]?.program !== prog) gl.deleteProgram(prog)
			resolve(result)
		}
		requestAnimationFrame(poll)
	})

// Links in flight on the GPU process's compile threads at once.
const LINKS_IN_FLIGHT = 4

/** Links the build of each shader name in `queue` on the stage off the main thread, its verdict cached as `compileCheck`'s. Resolves the names it could not take (no stage, or no parallel compile). */
const warmStageLinks = queue =>
	new Promise(resolve => {
		let waits = 60
		const start = () => {
			const renderer = globalThis.pixi_app?.renderer
			if (!renderer) return waits-- > 0 ? setTimeout(start, 1000) : resolve(queue)
			const filter = fieldFilterOf()
			let running = 0
			let stopped = false
			const next = () => {
				if (stopped || !queue.length) return running || resolve(queue)
				const item = queue.shift()
				const shader = getShader(item)
				const variant = shader && variantOf(shader)
				if (!variant) return next()
				const key = `${variant.key}|${filter}`
				running++
				linkOffThread(renderer, programFor(variant, filter)).then(result => {
					running--
					if (result === null) {
						stopped = true
						queue.unshift(item)
						return running || resolve(queue)
					}
					compileResults.set(key, result)
					next()
				})
			}
			for (let i = 0; i < LINKS_IN_FLIGHT; i++) next()
		}
		start()
	})

// Links in flight for nodes waiting to draw, by build key.
const pendingLinks = new Map()

const linksSettled = () => Promise.all([...pendingLinks.values()])

/** Whether a build has no verdict yet, so its node waits dark for `linkBuild`. */
const linksOffThread = (variant, filter) => !compileResults.has(`${variant.key}|${filter}`)

/** The build's verdict once linked on the stage off the main thread, else `probeLink`'s. */
const linkBuild = (variant, filter) => {
	const key = `${variant.key}|${filter}`
	let pending = pendingLinks.get(key)
	if (!pending) {
		pending = linkOffThread(globalThis.pixi_app?.renderer, programFor(variant, filter)).then(result => {
			pendingLinks.delete(key)
			if (result === null) return probeLink(variant, filter)
			compileResults.set(key, result)
			return result
		})
		pendingLinks.set(key, pending)
	}
	return pending
}

// Object ids whose node waits dark for its link, one entry per node: the stage overlay spins on each.
const linkingNodes = new Map()
const linkingListeners = new Set()
let linkingIds = []

const setLinking = (node, id) => {
	if (id == null) return
	if (id === false) {
		if (!linkingNodes.delete(node)) return
	} else linkingNodes.set(node, id)
	linkingIds = [...new Set(linkingNodes.values())]
	for (const listener of linkingListeners) listener()
}

export const subscribeLinking = listener => {
	linkingListeners.add(listener)
	return () => linkingListeners.delete(listener)
}

/** The ids of objects whose shader is still linking; a stable array between changes. */
export const linkingObjectIds = () => linkingIds

/** Keeps `node` dark until its build links, so its first draw never compiles on the main thread; a failed link leaves it dark. `node._linked` resolves whether it may draw. */
const awaitLink = (node, variant, filter, name, id) => {
	node._linking = true
	node.renderable = false
	setLinking(node, id)
	node.once('destroyed', () => setLinking(node, false))
	node._linked = linkBuild(variant, filter).then(result => {
		setLinking(node, false)
		if (node.destroyed) return false
		if (result !== true) {
			logger.error('shader failed to compile, skipping', name, result)
			return false
		}
		node._linking = false
		node.renderable = node._draws.length > 0
		return true
	})
}

/** Pre-compile every registered shader's build off the interactive path, so a live source swap is a cache hit (every subset and path shares the pipeline build): on the stage through parallel compile, else one by one on a probe context (`probeLink`). */
export const warmShaderCompileCache = async names => {
	// The stage's own builds link first.
	await linksSettled()
	// One name per build: the subsets all name the pipeline's.
	const builds = new Map()
	for (const name of names) {
		const shader = getShader(name)
		if (shader && !builds.has(variantOf(shader).key)) builds.set(variantOf(shader).key, name)
	}
	const queue = await warmStageLinks([...builds.values()])
	const filter = fieldFilterOf()
	for (const name of queue) await probeLink(variantOf(getShader(name)), filter)
}

// `<source>|<field filter>` for each program that has drawn once on the stage's renderer.
const drawnPrograms = new Set()
let drawWarmGeneration = 0

const afterFrames = (n, fn) => (n ? requestAnimationFrame(() => afterFrames(n - 1, fn)) : fn())

/** Draw each shader's build once off screen at idle: the GPU builds a program at its first draw, and a cold build stalls frames for seconds, which inside a fade leaps the outgoing clock and the fade. */
export const warmShaderDraws = sources => {
	const gen = ++drawWarmGeneration
	// A standalone shader by its source; every subset shares the pipeline build, drawn once.
	const keyOf = source => {
		const shader = getShader(source)
		return shader?.locks ? variantOf(shader).key : source
	}
	const queue = [...new Set(sources)].map(source => ({ source, key: keyOf(source) }))
	// A client with no stage (a phone) gives up after a minute.
	let waits = 60
	const step = () => {
		if (gen !== drawWarmGeneration) return
		const renderer = globalThis.pixi_app?.renderer
		if (!renderer) return waits-- > 0 && setTimeout(step, 1000)
		if (globalThis.pixi_transition_active) return schedule()
		const filter = fieldFilterOf()
		let item
		while ((item = queue.shift()) && drawnPrograms.has(`${item.key}|${filter}`));
		if (!item) return
		const { source } = item
		drawnPrograms.add(`${item.key}|${filter}`)
		const node = createShaderMesh({ source, width: 8, height: 8, x: 0, y: 0, scaleX: 1, scaleY: 1 }, {})
		// The next draw waits for frames to resume, so two cold builds never stack into one stall.
		const next = () => queue.length && afterFrames(2, schedule)
		if (!node) return next()
		Promise.resolve(node._linked ?? true).then(linked => {
			try {
				if (linked) renderer.generateTexture({ target: node, resolution: 1 }).destroy(true)
			} catch (e) {
				logger.warn('shader draw warm failed', source, e)
			} finally {
				node.destroy()
			}
			next()
		})
	}
	const schedule = () =>
		typeof requestIdleCallback === 'function' ? requestIdleCallback(step) : setTimeout(step, 50)
	whenAppLoaded().then(linksSettled).then(schedule)
}

/** Drops the cached builds of shaders whose module changed, so the next node compiles the new fragment. */
export const forgetShaderBuilds = names => {
	for (const cache of [programs, compileResults])
		for (const key of [...cache.keys()]) if (names.includes(key.split('|')[0])) cache.delete(key)
	for (const key of [...drawnPrograms])
		if (names.includes(key.split('|')[0].slice(SHADER_PREFIX.length))) drawnPrograms.delete(key)
}

// Keyed `variant|filter`: a standalone shader's variant key is its name, so `forgetShaderBuilds` drops it by name; a subset's folds in the pipeline source, so a stage edit is a new key.
let scaffold = false

/** Every pipeline fill draws its scaffold build (`scaffoldVariant`) while on; the clock and uniforms are kept. Returns the nodes switched. */
export const setShaderScaffold = on => {
	scaffold = Boolean(on)
	let switched = 0
	for (const node of mounted) {
		if (node.destroyed || !node._variant?.free || !node._draws) continue
		const program = programFor(node._variant, node._fieldFilter)
		node._program = program
		node.shader.glProgram = program
		for (const mesh of node._draws) if (!mesh.destroyed) mesh.shader.glProgram = program
		switched++
	}
	return switched
}

const programFor = (built, filter) => {
	const variant = scaffold ? scaffoldVariant(built) : built
	const key = `${variant.key}|${filter}`
	let program = programs.get(key)
	if (!program) {
		program = GlProgram.from({ vertex: SURFACE_VERTEX, fragment: buildFragment(variant, filter) })
		programs.set(key, program)
	}
	return program
}

const pickProgramFor = (variant, filter, axis) => {
	const key = `${variant.key}|${filter}|pick${axis}`
	let program = programs.get(key)
	if (!program) {
		program = GlProgram.from({
			vertex: SURFACE_VERTEX,
			fragment: pickFragment(buildFragment(variant, filter), axis),
		})
		programs.set(key, program)
	}
	return program
}

let pickTarget = null

// A shader's resources by name: Pixi defines each as a non-enumerable accessor, so a spread or `Object.assign` copies none.
const resourcesOf = shader => {
	const r = shader.resources
	return Object.fromEntries(Object.getOwnPropertyNames(r).map(k => [k, r[k]]))
}

// A draw's pick mesh: its own geometry and groups under the pick program, placed where the draw is.
const pickMeshOf = (draw, program) => {
	let p = draw._pick
	if (!p || p.program !== program) {
		if (p) p.wrap.destroy({ children: true })
		const mesh = new Mesh({
			geometry: draw.geometry,
			shader: new Shader({ glProgram: program, resources: resourcesOf(draw.shader) }),
		})
		const wrap = new Container()
		wrap.addChild(mesh)
		p = draw._pick = { program, mesh, wrap }
		draw.once('destroyed', () => p.wrap.destroy({ children: true }))
	}
	const pickResources = p.mesh.shader.resources
	for (const [k, v] of Object.entries(resourcesOf(draw.shader))) if (pickResources[k] !== v) pickResources[k] = v
	p.mesh.setFromMatrix(draw.worldTransform)
	return p
}

/** The pattern coordinate ([across, along] cells, null per axis off the pattern) of `node` at the stage point `point`, drawn through its real draws and groups by the pick build; null for a node with no pipeline build. `near` unwraps each axis. */
export const pickPattern = (node, point, { axes = [0, 1], near = [0, 0] } = {}) => {
	const renderer = globalThis.pixi_app?.renderer
	if (!renderer || !node?.shader || node.destroyed || !node._variant?.locks) return null
	pickTarget ??= RenderTexture.create({ width: 1, height: 1, resolution: 1 })
	const transform = new Matrix().translate(0.5 - point.x, 0.5 - point.y)
	writeFrameUniforms(node)
	return [0, 1].map(axis => {
		if (!axes.includes(axis)) return null
		const program = pickProgramFor(node._variant, node._fieldFilter, axis)
		for (const draw of node._draws) {
			if (draw.destroyed) continue
			const { wrap } = pickMeshOf(draw, program)
			renderer.render({ container: wrap, target: pickTarget, clear: true, clearColor: [0, 0, 0, 0], transform })
			const v = decodePick(renderer.extract.pixels(pickTarget).pixels, near[axis] ?? 0)
			if (v !== null) return v
		}
		return null
	})
}

/** Moves `key`'s accumulator by `delta` on every node of shader `name`, folded and carried as `tickShaders` folds a frame's travel. */
export const nudgeIntegrated = (name, key, delta) => {
	if (!Number.isFinite(delta) || !delta) return
	for (const node of mounted) {
		if (node.destroyed || node._shaderName !== name || !node.shader) continue
		const p = node._integrated?.find(q => q.key === key)
		if (!p) continue
		const u = node._nodeUniforms.uniforms
		// The scrub's travel carries the `along` integrators riding it.
		const travel = delta * perUnit(p.per, u)
		for (const q of node._integrated)
			if (q.along === key) {
				const rate = (q.rate ?? node._integratedRates[q.key]) * q.base * alongScale(q, u)
				if (Number.isFinite(rate) && rate) nudgeOne(node, u, q, travel * rate)
			}
		nudgeOne(node, u, p, delta)
		if (node._trail?.spec.by.includes(key)) advanceTrail(node._trail.spec, node._trail.state, u, delta)
	}
}

const nudgeOne = (node, u, { uniform, cycle, carry }, delta) => {
	const acc = node._integratedValues[uniform] + delta
	node._integratedValues[uniform] = wrapCycle(acc, cycle)
	u[uniform] = node._integratedValues[uniform]
	if (carry) {
		node._integratedTurns[uniform] += Math.round((acc - u[uniform]) / cycle)
		u[turnsUniform(uniform)] = node._integratedTurns[uniform]
	}
}

/** Every live node of shader `name`. */
export const shaderNodesOf = name => [...mounted].filter(n => !n.destroyed && n.shader && n._shaderName === name)

// Approach speed for a `settle` integrator coasting to rest at rate 0 — ~1/e of the gap remaining per 1/8 s.
const SETTLE_RATE = 8

// Every live shader node, registered on create and dropped on Pixi's 'destroyed'. A Set instead of a stage walk, so shaders nested in groups still tick.
const mounted = new Set()

/** The first live shader node (its Container), or null — the probe's store-vs-stage truth. */
export const mountedShaderMesh = () => {
	for (const node of mounted) if (!node.destroyed) return node
	return null
}

// The placement a stage-framed reference reads; the rect needs only the size.
const POSE_KEYS = ['x', 'y', 'width', 'height', 'rotation', 'scaleX', 'scaleY', 'pivotX', 'pivotY']

// Mapped: the references' outlines through `env` (the live objects), on stage. Unmapped: the node's local rect, placed by the node transform.
const geometryKey = (refs, pose, env, aspect) =>
	refs ? `surfaces|${surfaceGeometryKey(refs, env, nodeQuad(pose), aspect)}` : `rect|${pose.width}|${pose.height}`

const packDraws = (refs, pose, env, aspect) =>
	refs ? buildSurfaceDraws(refs, env, nodeQuad(pose), aspect) : buildRectDraws(pose.width, pose.height)

// What one draw reads besides the node's shared values: its field's rectangle and placement (`uToField` stage → field, `uFieldScale` field → stage distance), its frame, the frame's extent (`uResolution`), the outline's inradius and perimeter, the reference's slot and whether it reads the flow channels.
const drawUniforms = () =>
	new UniformGroup({
		uFieldRect: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
		uFieldRectTo: { value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>' },
		uFrame: { value: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]), type: 'mat3x3<f32>' },
		uFrameTo: { value: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]), type: 'mat3x3<f32>' },
		uToField: { value: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]), type: 'mat3x3<f32>' },
		uToFieldTo: { value: new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]), type: 'mat3x3<f32>' },
		uFieldScale: { value: 1, type: 'f32' },
		uFieldScaleTo: { value: 1, type: 'f32' },
		uExtent: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
		uExtentTo: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
		uInradius: { value: 1, type: 'f32' },
		uInradiusTo: { value: 1, type: 'f32' },
		uPerimeter: { value: 1, type: 'f32' },
		uPerimeterTo: { value: 1, type: 'f32' },
		uIndex: { value: 0, type: 'f32' },
		// 1 when the draw reads `vUV` from the field's flow channels (g, b) instead of its frame.
		uFlow: { value: 0, type: 'f32' },
		uFieldMorph: { value: 0, type: 'f32' },
		// A flow draw's picture fit on (u, v): scale.xy, offset.zw.
		uFlowCover: { value: new Float32Array([1, 1, 0, 0]), type: 'vec4<f32>' },
		uFlowCoverTo: { value: new Float32Array([1, 1, 0, 0]), type: 'vec4<f32>' },
		// frameUniforms: written before each render (`writeFrameUniforms`).
		uFoldLead: { value: 0, type: 'f32' },
	})

// Every draw's frameUniforms at its extent as surfaceFragment mixes it (uExtent → uExtentTo by uFieldMorph), from the node's values as they stand at this render.
const writeFrameUniforms = node => {
	const values = node._nodeUniforms.uniforms
	for (const mesh of node._draws) {
		if (mesh.destroyed) continue
		const u = mesh.shader.resources.surface.uniforms
		const m = u.uFieldMorph
		u.uFoldLead = frameUniforms(
			values,
			[0, 1].map(i => u.uExtent[i] * (1 - m) + u.uExtentTo[i] * m)
		).uFoldLead
	}
}

const EMPTY_QUAD = [
	[0, 0],
	[0, 0],
	[0, 0],
	[0, 0],
]

/**
 * One texture per field KEY (DECISIONS U2b: an evicted field rebaked under its key is a new object with the same content, so identity would re-upload for nothing), from the resident set in fieldTextures.js: shared with every node drawing that outline and with the field warm. References to one outline share it.
 * A key this node no longer wants, held by nothing else, hands its texture to a new key of the same size, re-uploaded in place (a drag walks keys at one size); what is left over is destroyed, but only after `rebind` has pointed every mesh at its new source (a destroyed bound source kills the mesh's bind group, U4h).
 */
const bindFields = (node, draws, rebind) => {
	const wanted = new Map()
	for (const d of draws) if (!wanted.has(d.key)) wanted.set(d.key, d.field)
	bindFieldTextures(node._fieldTextures, wanted, node._fieldFilter, rebind)
}

const NO_COVER = [1, 1, 0, 0]

// Never a zero divisor in a body.
const perimeterOf = field => (Number.isFinite(field.perimeter) && field.perimeter > 0 ? field.perimeter : 1)

const unionQuad = (a, b) => {
	const points = [...a, ...b]
	const xs = points.map(([x]) => x)
	const ys = points.map(([, y]) => y)
	const x0 = Math.min(...xs)
	const x1 = Math.max(...xs)
	const y0 = Math.min(...ys)
	const y1 = Math.max(...ys)
	return [
		[x0, y0],
		[x1, y0],
		[x1, y1],
		[x0, y1],
	]
}

const releaseMorphTextures = node => {
	const morph = node._fieldMorph
	if (!morph) return
	for (const mesh of node._draws) {
		if (mesh.destroyed) continue
		const resources = mesh.shader.resources
		resources.uFieldTo = resources.uField
		resources.surface.uniforms.uFieldMorph = 0
	}
	for (const [key, source] of morph.held) releaseFieldTexture(key, source, node._fieldFilter)
	node._fieldMorph = null
}

const writeDraw = (node, mesh, { index, frame, field, flow, cover, quad, toField, scale }, source) => {
	const u = mesh.shader.resources.surface.uniforms
	const target = node._fieldMorph?.targets.get(index)
	u.uFieldRect.set(fieldRect(field))
	u.uFrame.set(frameUniform(frame.matrix))
	u.uToField.set(frameUniform(toField))
	u.uFieldScale = scale
	u.uExtent.set(frame.extent)
	// A sliver bakes inradius 0: never a zero divisor in a body.
	u.uInradius = (Number.isFinite(field.inradius) && field.inradius > 0 ? field.inradius : 1) * scale
	u.uPerimeter = perimeterOf(field) * scale
	u.uIndex = index
	u.uFlow = flow ? 1 : 0
	u.uFlowCover.set(cover ?? NO_COVER)
	if (target) {
		const to = target.draw
		u.uFieldRectTo.set(fieldRect(to.field))
		u.uFrameTo.set(frameUniform(to.frame.matrix))
		u.uToFieldTo.set(frameUniform(to.toField))
		u.uFieldScaleTo = to.scale
		u.uExtentTo.set(to.frame.extent)
		u.uInradiusTo = (Number.isFinite(to.field.inradius) && to.field.inradius > 0 ? to.field.inradius : 1) * to.scale
		u.uPerimeterTo = perimeterOf(to.field) * to.scale
		u.uFlowCoverTo.set(to.cover ?? NO_COVER)
		u.uFieldMorph = node._fieldMorph.progress
		if (mesh.shader.resources.uFieldTo !== target.source) mesh.shader.resources.uFieldTo = target.source
	} else {
		u.uFieldRectTo.set(fieldRect(field))
		u.uFrameTo.set(frameUniform(frame.matrix))
		u.uToFieldTo.set(frameUniform(toField))
		u.uFieldScaleTo = scale
		u.uExtentTo.set(frame.extent)
		u.uInradiusTo = (Number.isFinite(field.inradius) && field.inradius > 0 ? field.inradius : 1) * scale
		u.uPerimeterTo = perimeterOf(field) * scale
		u.uFlowCoverTo.set(cover ?? NO_COVER)
		u.uFieldMorph = 0
		if (mesh.shader.resources.uFieldTo !== source) mesh.shader.resources.uFieldTo = source
	}
	if (mesh.shader.resources.uField !== source) mesh.shader.resources.uField = source
	refillQuad(mesh.geometry, target ? unionQuad(quad, target.draw.quad) : quad)
}

// One mesh per draw. Its quad and per-draw group are its own; the program, the node's group (params, clock, uSurfaceCount, uView) and the field textures are the node's, so every node write reaches every draw.
const drawMesh = node => {
	const geometry = quadGeometry(EMPTY_QUAD)
	const shader = new Shader({
		glProgram: node._program,
		resources: {
			uniforms: node._nodeUniforms,
			surface: drawUniforms(),
			uField: Texture.EMPTY.source,
			uFieldTo: Texture.EMPTY.source,
			...(node._sample ? { uTexture: node._sampleTexture } : {}),
		},
	})
	const mesh = new Mesh({ geometry, shader })
	mesh.label = `${node.label}#draw`
	// Mesh.destroy only NULLS _geometry/_shader — freeing them is ours, or every rebuild leaks GPU memory. The shared program, group and textures stay.
	mesh.once('destroyed', () => {
		geometry.destroy(true)
		shader.destroy()
	})
	return mesh
}

/**
 * The node: a Container of one Mesh per draw. Everything a reader outside this module addresses lives on it — the clock (`_shaderName`, `_shaderTime`, `_integrated*`), `_surfaced`, the transform, alpha, mask — and `shader` is a facade over the node's program and shared group (`shader.resources.uniforms`, `shader.glProgram`), so the fade proxy, automation and the probe write one place.
 */
const shaderNode = (glProgram, group, filter, { sample = false } = {}) => {
	const node = new Container()
	node._program = glProgram
	node._nodeUniforms = group
	node._fieldFilter = filter
	node._sample = sample
	node._sampleTexture = sample ? Texture.EMPTY.source : null
	node._draws = []
	node._fieldTextures = new Map()
	node._fieldMorph = null
	node.shader = { glProgram, resources: { uniforms: group } }
	if (!sample) node.onRender = () => writeFrameUniforms(node)
	// Container.destroy only detaches children: the draws and the textures are freed here. `shader` goes null, which is what readers test for a dead backing.
	node.once('destroyed', () => {
		for (const mesh of node._draws.splice(0)) if (!mesh.destroyed) mesh.destroy()
		if (node._fieldMorph) releaseMorphTextures(node)
		releaseFieldTextures(node._fieldTextures, node._fieldFilter)
		node.shader = null
	})
	return node
}

const applyDraws = (node, draws) => {
	const meshes = node._draws
	while (meshes.length > draws.length) meshes.pop().destroy()
	while (meshes.length < draws.length) {
		const mesh = drawMesh(node)
		node.addChild(mesh)
		meshes.push(mesh)
	}
	// The writes rebind `uField` inside the bind, before a texture this node let go is destroyed.
	bindFields(node, draws, () =>
		draws.forEach((draw, i) => writeDraw(node, meshes[i], draw, node._fieldTextures.get(draw.key)))
	)
}

const markGeometry = (node, refs, packed, key) => {
	node._geometryKey = key
	node._surfaced = !!refs
	applyDraws(node, packed.draws)
	// Fail dark: every referenced outline missing paints nothing, never the whole node. A non-renderable container skips its children.
	node.renderable = packed.draws.length > 0 && !node._linking
	node._nodeUniforms.uniforms.uSurfaceCount = refs ? refs.length : 1
}

// Rebuilds only when the key moved, so an unrelated store write costs one key compare. Mapping on/off swaps draws under the same program; a placement change re-places draws and never rebakes (the field cache and the textures are keyed by geometry).
const syncGeometry = (node, refs, pose, env, aspect) => {
	// What a mid-fade pose needs to re-place mapped geometry without the store.
	node._mapping = { refs, env, pose }
	// A settled blend's draws still hold its FROM geometry: released, a key past them rebuilds.
	if (node._fieldMorph?.settled) releaseMorphTextures(node)
	const key = geometryKey(refs, pose, env, aspect)
	if (key !== node._geometryKey) markGeometry(node, refs, packDraws(refs, pose, env, aspect), key)
}

const morphAspect = node => (node._sample ? textureAspect(node) : undefined)

const morphPose = (node, obj) =>
	node._sample
		? samplePose(Object.fromEntries(SAMPLE_POSE_KEYS.map(k => [k, obj[k]])))
		: Object.fromEntries(POSE_KEYS.map(k => [k, obj[k] ?? getObjectInitial(obj.type, k)]))

/** Prepare a GPU blend between two baked mapped fields for the same build and reference layout; false when the geometry does not change. */
export const beginShaderFieldMorph = (node, toObj, toObjects) => {
	if (!node?.shader || node._fieldMorph || !node._mapping?.refs?.length) return false
	const fromRefs = node._mapping.refs
	const toRefs = nodeRefs(toObj)
	if (!toRefs?.length || fromRefs.length !== toRefs.length) return false
	if (fromRefs.some((ref, i) => refKey(ref) !== refKey(toRefs[i]))) return false

	const aspect = morphAspect(node)
	const fromEnv = node._mapping.env
	const toEnvMap = { ...fromEnv, objects: toObjects }
	const fromPose = node._mapping.pose
	const toPose = morphPose(node, toObj)
	if (geometryKey(fromRefs, fromPose, fromEnv, aspect) === geometryKey(toRefs, toPose, toEnvMap, aspect)) return false
	const fromDraws = packDraws(fromRefs, fromPose, fromEnv, aspect).draws
	const toDraws = packDraws(toRefs, toPose, toEnvMap, aspect).draws
	if (fromDraws.length !== toDraws.length || !fromDraws.length) return false
	if (fromDraws.some((draw, i) => draw.index !== toDraws[i].index || draw.flow !== toDraws[i].flow)) return false

	const held = new Map()
	const targets = new Map()
	try {
		for (let i = 0; i < toDraws.length; i++) {
			const draw = toDraws[i]
			if (!held.has(draw.key)) held.set(draw.key, acquireFieldTexture(draw.key, draw.field, node._fieldFilter))
			targets.set(draw.index, { draw, source: held.get(draw.key) })
		}
	} catch (error) {
		for (const [key, source] of held) releaseFieldTexture(key, source, node._fieldFilter)
		throw error
	}

	node._fieldMorph = { targets, held, progress: 0, settled: false }
	for (let i = 0; i < fromDraws.length; i++)
		writeDraw(node, node._draws[i], fromDraws[i], node._fieldTextures.get(fromDraws[i].key))
	return true
}

export const setShaderFieldMorphProgress = (node, progress) => {
	const morph = node?._fieldMorph
	if (!morph) return
	morph.progress = Math.max(0, Math.min(1, progress))
	for (const mesh of node._draws) {
		if (!mesh.destroyed) mesh.shader.resources.surface.uniforms.uFieldMorph = morph.progress
	}
}

export const keyShaderFieldMorph = (node, fromObj, fromObjects, toObj, toObjects) => {
	if (!node?.shader || node.destroyed || !node._mapping?.refs) return false
	const base = node._fieldMorph?.base ?? node._mapping.env
	if (node._fieldMorph) releaseMorphTextures(node)
	const refs = nodeRefs(fromObj)
	const pose = morphPose(node, fromObj)
	const env = { ...base, objects: fromObjects }
	const aspect = morphAspect(node)
	node._mapping = { refs, env, pose }
	const key = geometryKey(refs, pose, env, aspect)
	if (key !== node._geometryKey) markGeometry(node, refs, packDraws(refs, pose, env, aspect), key)
	if (!beginShaderFieldMorph(node, toObj, toObjects)) {
		node._mapping = { refs, env: base, pose }
		return false
	}
	node._fieldMorph.base = base
	return true
}

/** Bakes what a later segment to `obj` in `objects` draws, so keying it only uploads. */
export const warmShaderFieldMorph = (node, obj, objects) => {
	if (!node?.shader || node.destroyed || !node._mapping?.refs) return
	const base = node._fieldMorph?.base ?? node._mapping.env
	packDraws(nodeRefs(obj), morphPose(node, obj), { ...base, objects }, morphAspect(node))
}

/** The nearer endpoint holds until the next geometry sync releases it. A carry releases now: the successor re-places the node at the live outline and blends from there. */
export const settleShaderFieldMorph = (node, { carry = false } = {}) => {
	const morph = node?._fieldMorph
	if (!morph) return
	if (morph.base) node._mapping = { ...node._mapping, env: morph.base }
	if (carry) return releaseMorphTextures(node)
	setShaderFieldMorphProgress(node, morph.progress < 0.5 ? 0 : 1)
	morph.settled = true
}

/** Create the backing Container for a shader-source object, or null for an unknown/uncompilable shader (caller skips the object). `env` is `{ surfaces, objects }`. */
export const createShaderMesh = (obj, env, { fill = false } = {}) => {
	const shader = getShader(obj.source)
	if (!shader) {
		logger.warn('unknown shader source', obj.source)
		return null
	}

	const values = sourceValues(obj, obj.source)
	const build = variantOf(shader)
	const filter = fieldFilterOf()
	const linking = linksOffThread(build, filter)
	const compiled = linking || compileCheck(build, null, filter)
	if (compiled !== true) {
		logger.error('shader failed to compile, skipping', shader.name, compiled)
		return null
	}

	const meshEnv = toEnv(env)
	const pose = Object.fromEntries(POSE_KEYS.map(k => [k, obj[k]]))
	const { spec, uniforms, trail, trailState } = buildUniforms(shader, build, values)

	try {
		const node = shaderNode(programFor(build, filter), new UniformGroup(uniforms), filter)
		node.label = `shader:${shader.name}`
		node._shaderName = shader.name
		node._spec = shader
		node._variant = build
		node._bound = spec.bound
		node._fill = fill
		const refs = nodeRefs(obj)
		markGeometry(node, refs, packDraws(refs, pose, meshEnv), geometryKey(refs, pose, meshEnv))
		node._mapping = { refs, env: meshEnv, pose }
		initClock(node, shader, spec, values, trail, trailState)
		if (linking) awaitLink(node, build, filter, shader.name, obj.id)
		mounted.add(node)
		node.once('destroyed', () => mounted.delete(node))
		return node
	} catch (e) {
		logger.error('failed to build shader mesh', shader.name, e)
		return null
	}
}

// The group a build binds: a subset's locked ids are constants in its GLSL and never upload.
const buildUniforms = (shader, build, values) => {
	const spec = { ...shader, bound: boundParams(shader, build) }

	// Uniform declarations derive from the registry params — the single place a shader's controls are defined.
	const uniforms = {
		uTime: { value: 0, type: 'f32' },
		uSurfaceCount: { value: 1, type: 'f32' },
		uStageUnit: { value: stageUnit(WIDTH, HEIGHT), type: 'f32' },
	}
	const uniformOf = value =>
		Array.isArray(value)
			? { value: new Float32Array(value), type: `vec${value.length}<f32>` }
			: { value, type: 'f32' }
	// A locked id is a constant in the GLSL, never uploaded; its value stays here for `per` to read.
	for (const [name, value] of Object.entries(lockUniforms(build))) uniforms[name] = uniformOf(value)
	for (const [key, def] of Object.entries(spec.bound)) {
		// An integrated uniform holds the accumulated distance, which starts at 0; the param is its rate.
		const value = def.integrate ? 0 : (values[key] ?? def.initial)
		uniforms[uniformName(key, def)] = uniformOf(uniformValue(value, def))
		if (def.carry) uniforms[turnsUniform(uniformName(key, def))] = { value: 0, type: 'f32' }
		if (def.live) uniforms[liveRateUniform(uniformName(key, def))] = { value: 0, type: 'f32' }
		if (def.depth) {
			uniforms[depthUniform(uniformName(key, def))] = { value: 0, type: 'f32' }
			uniforms[turnsUniform(depthUniform(uniformName(key, def)))] = { value: 0, type: 'f32' }
		}
		if (def.hold) {
			const names = heldUniforms(uniformName(key, def))
			uniforms[names.to] = { value, type: 'f32' }
			uniforms[names.from] = { value, type: 'f32' }
			uniforms[names.at] = { value: 0, type: 'f32' }
		}
	}
	const trail = trailOf(spec)
	let trailState = null
	if (trail) {
		const flat = Object.fromEntries(Object.entries(uniforms).map(([k, v]) => [k, v.value]))
		trailState = steadyTrail(trail, flat)
		for (const { uniform } of trail.travelers)
			uniforms[trailUniform(uniform)] = {
				value: flat[trailUniform(uniform)],
				type: 'vec4<f32>',
				size: trail.bins / 4,
			}
		uniforms[TRAIL_AT] = { value: flat[TRAIL_AT], type: 'f32' }
	}
	if (spec.bound.rows) Object.assign(uniforms, seaUniformDecls())
	return { spec, uniforms, trail, trailState }
}

const initClock = (node, shader, spec, values, trail, trailState) => {
	// Per-instance clock: two instances of one shader can run at different phases, and pausing must freeze without a jump.
	node._shaderTime = 0
	// Folds the `uTime` upload only; `_shaderTime` stays unfolded.
	node._clockCycle = shader.cycle
	node._shaderPaused = false
	// The `rate: true` param scales the CLOCK, read per tick, so a change bends time forward without a phase jump.
	node._rateUniform = rateUniformOf(spec)
	node._rateBase = rateBaseOf(spec)
	// An `integrate` param is a RATE; the uniform it names is the quantity this clock accumulates.
	node._integrated = integratedParamsOf(spec)
	node._integratedValues = {}
	node._integratedRates = {}
	node._integratedTurns = {}
	// The way each `coast` integrator last moved.
	node._coastDirs = {}
	for (const { uniform } of node._integrated) {
		node._integratedValues[uniform] = 0
		node._integratedTurns[uniform] = 0
	}
	node._sea = spec.bound.rows ? newSea() : null
	// A `hold` param's value in force, the one before it and the unfolded clock at the change.
	node._held = heldParamsOf(spec).map(p => {
		const value = values[p.key] ?? spec.bound[p.key].initial
		return { ...p, names: heldUniforms(p.uniform), to: value, from: value, at: 0 }
	})
	node._trail = trail && { spec: trail, state: trailState }
}

const CLOCK_FIELDS = ['_shaderTime', '_integratedValues', '_integratedTurns', '_coastDirs', '_sea', '_held', '_trail']

/** A shader node onto another build or params in place: its draws and geometry kept, its clock, integrators, sea, held values and trail (per uniform) carried. `shader` is what the build binds (`pathOf`'s union shader, or the module): on the node's own build a new one rebinds the group, never links. False, the node untouched, when the build fails to compile. */
export const switchShaderVariant = (node, shader, variant, values = {}) => {
	if (!node?.shader || node._sample || node.destroyed || !shader) return false
	const sameBuild = node._variant?.key === variant.key
	if (sameBuild && node._spec === shader) return true
	const filter = node._fieldFilter
	const compiled = sameBuild || compileCheck(variant, null, filter)
	if (compiled !== true) {
		logger.error('shader variant failed to compile, keeping the build', shader.name, compiled)
		return false
	}
	const { spec, uniforms, trail, trailState } = buildUniforms(shader, variant, values)
	// The blob field's lead on the first draw's frame, before and after: speed's travel takes the difference.
	const frame = shaderFrame(node)
	const leadOf = u => (frame ? fieldLead(u, frame.extent, frame.unit) : 0)
	const lead = leadOf(node._nodeUniforms.uniforms)
	uniforms.uSurfaceCount.value = node._nodeUniforms.uniforms.uSurfaceCount
	const from = Object.fromEntries(CLOCK_FIELDS.map(k => [k, node[k]]))
	const group = new UniformGroup(uniforms)
	const program = programFor(variant, filter)
	node._program = program
	node._nodeUniforms = group
	node.shader = { glProgram: program, resources: { uniforms: group } }
	for (const mesh of node._draws) {
		if (mesh.destroyed) continue
		mesh.shader.glProgram = program
		mesh.shader.resources.uniforms = group
	}
	node.label = `shader:${shader.name}`
	node._shaderName = shader.name
	node._spec = shader
	node._variant = variant
	node._bound = spec.bound
	const paused = node._shaderPaused
	initClock(node, shader, spec, values, trail, trailState)
	node._shaderPaused = paused
	carryClock(from, node)
	writeShaderValues(node, values)
	const u = node._nodeUniforms.uniforms
	const speed = node._integrated?.find(p => p.key === 'speed' && !p.along)
	const delta = lead - leadOf(u)
	if (speed && delta) nudgeOne(node, u, speed, delta)
	return true
}

// Tempo-locked, Speed ×1 runs one natural period per beat; a rate past the shader's flash-safe maximum halves until within, so it stays on the beat grid.
const lockFactor = (lock, speed) => {
	if (!lock || !Number.isFinite(speed)) return 1
	let f = lock.period / tempo.beatSeconds()
	while (Math.abs(speed * f) > lock.max && f > 1e-6) f /= 2
	return f
}

/** Advance every live shader's clock. MUST run per RENDERED frame (rides gsap.ticker) — updatePixiObject only fires on Redux change, which would leave a still frame. One write per node: its draws share the group. */
export const tickShaders = deltaMS => {
	for (const node of mounted) {
		if (node.destroyed) {
			mounted.delete(node)
			continue
		}
		if (node._shaderPaused) continue
		const u = node._nodeUniforms.uniforms
		const raw = node._rateUniform ? u[node._rateUniform] : 1
		const rate = node._rateUniform ? raw * lockFactor(node._speedLock, raw) : 1
		const dt = (deltaMS / 1000) * (Number.isFinite(rate) ? rate : 1) * node._rateBase
		node._shaderTime += dt
		u.uTime = wrapCycle(node._shaderTime, node._clockCycle)

		// Each integrator's travel this frame, for a trail its carriers move.
		const steps = {}
		// Each carrier's travel this frame in su, for the `along` integrators after it.
		const travels = {}
		const rowsBefore = (u.uSpeed ?? 0) + (u.uSpeedTurns ?? 0)
		for (const p of node._integrated) {
			const { key, uniform, settle, per, base, cycle, carry, wall, live, coast, along } = p
			const value = p.rate ?? node._integratedRates[key]
			const locked = key === node._speedLock?.key && !along ? value * lockFactor(node._speedLock, value) : value
			const perSecond = along ? locked * base * alongScale(p, u) : (locked * base) / perUnit(per, u)
			const r = Number.isFinite(perSecond) ? perSecond : 0
			if (live) u[liveRateUniform(uniform)] = r
			if (coast && r !== 0) node._coastDirs[uniform] = Math.sign(r)
			if (r === 0 && coast && u.uRows > 0) {
				const acc = node._integratedValues[uniform]
				const target = coastTarget(acc, u.uCount, node._coastDirs[uniform] ?? 1)
				const gap = target - acc
				node._integratedValues[uniform] =
					Math.abs(gap) < 1e-4 ? target : acc + gap * Math.min(1, (deltaMS / 1000) * SETTLE_RATE)
			} else if (r === 0 && settle) {
				const acc = node._integratedValues[uniform]
				const target = Math.round(acc / settle) * settle
				const gap = target - acc
				// Proportional approach on wall time: a negative clock rate would push it away from rest.
				node._integratedValues[uniform] =
					Math.abs(gap) < 1e-4 ? target : acc + gap * Math.min(1, (deltaMS / 1000) * SETTLE_RATE)
			} else {
				const step = along ? (travels[along] ?? 0) * r : (wall ? deltaMS / 1000 : dt) * r
				if (!along) {
					steps[key] = step
					travels[key] = step * perUnit(per, u)
				}
				node._integratedValues[uniform] += step
			}
			const acc = node._integratedValues[uniform]
			node._integratedValues[uniform] = wrapCycle(acc, cycle)
			u[uniform] = node._integratedValues[uniform]
			if (carry) {
				node._integratedTurns[uniform] += Math.round((acc - u[uniform]) / cycle)
				u[turnsUniform(uniform)] = node._integratedTurns[uniform]
			}
		}
		if (node._sea && u.uRows > 0) {
			const travel = u.uSpeed + u.uSpeedTurns
			stepSea(node._sea, u, {
				seconds: deltaMS / 1000,
				rate: node._integratedRates.wave_speed ?? 0,
				moved: travel - rowsBefore,
				travel,
			})
			uploadSea(node._sea, u, travel)
		}
		if (node._trail) {
			const { spec, state } = node._trail
			advanceTrail(
				spec,
				state,
				u,
				spec.by.reduce((s, key) => s + (steps[key] ?? 0), 0)
			)
		}
		tickHeld(node, u)
	}
}

/** Moves each `hold` param toward the value written to its own uniform without touching the steps around the clock: within the change's step `To` simply follows; `steps` steps away the change is behind the picture, so `From` catches up and a new change may start; in between the write waits. */
const tickHeld = (node, u) => {
	for (const h of node._held ?? []) {
		const away = Math.abs(Math.floor(node._shaderTime / h.step) - Math.floor(h.at / h.step))
		if (away >= h.steps) h.from = h.to
		const target = u[h.uniform]
		if (target !== h.to && Number.isFinite(target)) {
			if (away === 0) h.to = target
			else if (away >= h.steps) {
				h.to = target
				h.at = node._shaderTime
			}
		}
		u[h.names.to] = h.to
		u[h.names.from] = h.from
		u[h.names.at] = wrapCycle(h.at, node._clockCycle)
	}
}

/** Hand one shader node's clock and integrated quantities to its replacement, so a geometry-only rebuild never restarts the picture. No-op across different shaders. */
export const carryShaderClock = (from, to) => {
	if (!from?._shaderName || from._sample || from._shaderName !== to?._shaderName || from === to) return
	carryClock(from, to)
}

// `from` is a node or a switch's snapshot of one: the trail carries per uniform, a traveler `from` lacks starting steady.
const carryClock = (from, to) => {
	to._shaderTime = from._shaderTime
	// Only what `to` declares: a hot-swapped module may add or drop params.
	for (const { uniform } of to._integrated ?? []) {
		to._integratedValues[uniform] = from._integratedValues[uniform] ?? 0
		to._integratedTurns[uniform] = from._integratedTurns[uniform] ?? 0
	}
	to._coastDirs = { ...from._coastDirs }
	if (to._sea) to._sea = from._sea ?? newSea()
	to._held = (to._held ?? []).map(h => {
		const old = from._held?.find(f => f.key === h.key)
		return old ? { ...h, to: old.to, from: old.from, at: old.at } : h
	})
	const u = to.shader?.resources?.uniforms?.uniforms
	if (!u) return
	// The train as it stood; a module that changed its ring starts one steady at the same travel.
	if (to._trail && from._trail) {
		const { spec } = to._trail
		const same = from._trail.spec.bins === spec.bins && from._trail.spec.bin === spec.bin
		to._trail.state = steadyTrail(spec, u, from._trail.state.at)
		if (same)
			for (const { uniform } of spec.travelers) {
				const old = from._trail.state.slots[uniform]
				if (old) to._trail.state.slots[uniform] = old.slice()
			}
		for (const { uniform } of spec.travelers) u[trailUniform(uniform)] = to._trail.state.slots[uniform]
		advanceTrail(spec, to._trail.state, u, 0)
	}
	u.uTime = wrapCycle(to._shaderTime, to._clockCycle)
	tickHeld(to, u)
	for (const { uniform, carry } of to._integrated ?? []) {
		u[uniform] = to._integratedValues[uniform]
		if (carry) u[turnsUniform(uniform)] = to._integratedTurns[uniform]
	}
	if (to._sea) uploadSea(to._sea, u, (u.uSpeed ?? 0) + (u.uSpeedTurns ?? 0))
}

/** Place a shader node at a resolved pose. Size lives in the quad, so scaleXY carries only the flip sign; a shader has no picture to zoom or pan. The quad spans `0…w × 0…h`, so the Container's pivot is the stored pivot and the mirror turns about it. A mapped node re-places only its stage-framed geometry and, for `sample`, its view — what a fade's per-frame pose moves. */
export const composeShaderMesh = (node, pose) => {
	// A dead node has no shader; one this module did not build (a fade test's fake) has no draws to place.
	if (!node.shader || !node._draws) return
	const { x, y, width, height, scaleX, scaleY, pivotX, pivotY } = pose
	// Surfaced geometry is already in stage coordinates. Known limit: a parent group's transform still moves it.
	if (node._surfaced) {
		node.position.set(0, 0)
		node.pivot.set(0, 0)
		node.scale.set(1, 1)
		node.rotation = 0
		const { refs, env, pose: last } = node._mapping
		const placed = { ...last, ...pose }
		if (node._sample) {
			syncGeometry(node, refs, samplePose(placed), env, textureAspect(node))
			writeView(node, placed)
		} else syncGeometry(node, refs, placed, env)
		return
	}
	syncGeometry(node, null, { width, height })
	node.x = x
	node.y = y
	node.pivot.set((pivotX ?? 0.5) * width, (pivotY ?? 0.5) * height)
	node.scale.x = scaleX
	node.scale.y = scaleY
}

/** A shader fill's update. `init` is the caller's resolver; `env` as for createShaderMesh. */
export const updateShaderMesh = (node, obj, init, env) => {
	const shader = getShader(obj.source)
	if (!shader || !node.shader) return

	const pose = Object.fromEntries(POSE_KEYS.map(k => [k, init(k)]))
	syncGeometry(node, nodeRefs(obj), pose, toEnv(env))
	composeShaderMesh(node, pose)
	node._shaderPaused = init('paused') === true

	const values = sourceValues(obj, obj.source)
	// Another shader, or the module reloaded: the node rebinds in place. A path fade owns its node's build.
	if (!node._path && shader !== node._spec) if (switchShaderVariant(node, shader, variantOf(shader), values)) return
	writeShaderValues(node, values)
}

/** The frame a shader node's first draw paints (`countFit`'s): its extent and one su, stage units. */
export const shaderFrame = node => {
	const draw = node._draws?.find(m => !m.destroyed)
	if (!draw) return null
	return { extent: [...draw.shader.resources.surface.uniforms.uExtent], unit: node._nodeUniforms.uniforms.uStageUnit }
}

/** A shader node's params from a values bag, absent keys at their initial. uTime and every integrated uniform are owned by tickShaders: an integrated param sets only its rate. */
export const writeShaderValues = (node, values) => {
	const shader = getShader(node._shaderName)
	if (!shader || !node.shader || node.destroyed) return
	const u = node._nodeUniforms.uniforms
	node._integratedRates = {}
	const speed = shader.params.speed
	node._speedLock =
		values[SPEED_SYNC] === true && speed?.period > 0
			? { key: 'speed', period: speed.period, max: speed.maximum }
			: null
	// The node's build binds these: a subset's locked ids are constants, never written.
	for (const [key, def] of Object.entries(node._bound ?? shader.params)) {
		const name = uniformName(key, def)
		if (!(name in u)) continue
		const value = values[key] ?? def.initial
		if (def.integrate) {
			node._integratedRates[key] = value
			// A paused node never ticks, so a live rate is in force from the write.
			const p = def.live && node._integrated?.find(q => q.key === key)
			if (p)
				u[liveRateUniform(p.uniform)] = p.along
					? value * p.base * alongScale(p, u)
					: (value * p.base) / perUnit(p.per, u)
			continue
		}
		const v = uniformValue(value, def)
		if (Array.isArray(v)) u[name].set(v)
		else u[name] = v
	}
	// A paused node never ticks, so rows' window is count's from the write.
	if (node._sea && node._shaderPaused) {
		node._sea.win = null
		uploadSea(node._sea, u, (u.uSpeed ?? 0) + (u.uSpeedTurns ?? 0))
	}
}

// A mapped camera or file: the source's texture through the surfaced build. `uView` is the node's zoom/pan/mirror over the frame (xy scale, zw offset), so a pose change never rebuilds geometry. Outside the picture is clear, as beside the sprite.
const SAMPLE = {
	name: '@sample',
	fragment: `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 fragColor;
uniform vec4 uColor;
uniform vec4 uView;
uniform sampler2D uTexture;

void main() {
	vec2 t = vUV * uView.xy + uView.zw;
	if (any(lessThan(t, vec2(0.0))) || any(greaterThan(t, vec2(1.0)))) discard;
	fragColor = texture(uTexture, t) * uColor;
}`,
}

const SAMPLE_POSE_KEYS = [...POSE_KEYS, 'zoom', 'panX', 'panY']

// The mirror rides `uView`, so the stage frame is the unmirrored box.
const samplePose = pose => ({ ...pose, scaleX: 1, scaleY: 1 })

const writeView = (node, { width, height, zoom, panX, panY, scaleX, scaleY }) => {
	const z = (zoom || 100) / 100
	const fx = scaleX < 0 ? -1 : 1
	const fy = scaleY < 0 ? -1 : 1
	const ox = -(panX || 0) / (width || 1)
	const oy = -(panY || 0) / (height || 1)
	node._nodeUniforms.uniforms.uView.set([fx / z, fy / z, fx < 0 ? 1 - ox : ox, fy < 0 ? 1 - oy : oy])
}

/** A sample node's view from its source's bag, its geometry untouched: what a fade frame or a gesture moves mid-fade. */
export const writeSampleValues = (node, values) => {
	if (!node?.shader || node.destroyed || !node._mapping) return
	const { width, height } = node._mapping.pose
	writeView(node, {
		width,
		height,
		zoom: values.zoom,
		panX: values.panX,
		panY: values.panY,
		scaleX: values.mirror ? -1 : 1,
	})
}

// The bound picture's aspect: what a surface-framed reference covers with.
const textureAspect = node => (node?._sourceId ? sourceAspect(node._sourceId) : null)

/** The backing Container for a mapped texture source, or null when the build fails. `bindSampleTexture` gives it its picture. */
export const createSampleMesh = (obj, env, { fill = false } = {}) => {
	const filter = fieldFilterOf()
	const linking = linksOffThread(variantOf(SAMPLE), filter)
	const compiled = linking || compileCheck(variantOf(SAMPLE), null, filter)
	if (compiled !== true) {
		logger.error('sample build failed to compile', compiled)
		return null
	}
	const meshEnv = toEnv(env)
	const pose = Object.fromEntries(SAMPLE_POSE_KEYS.map(k => [k, obj[k]]))
	const uniforms = {
		uView: { value: new Float32Array([1, 1, 0, 0]), type: 'vec4<f32>' },
		uSurfaceCount: { value: 1, type: 'f32' },
	}
	try {
		const node = shaderNode(programFor(variantOf(SAMPLE), filter), new UniformGroup(uniforms), filter, {
			sample: true,
		})
		node.label = 'sample'
		// A program like any shader's: fades freeze it by rendering and re-place its ghost as quad-sized.
		node._shaderName = SAMPLE.name
		node._fill = fill
		const refs = nodeRefs(obj)
		const aspect = textureAspect(node)
		markGeometry(
			node,
			refs,
			packDraws(refs, samplePose(pose), meshEnv, aspect),
			geometryKey(refs, samplePose(pose), meshEnv, aspect)
		)
		node._mapping = { refs, env: meshEnv, pose: samplePose(pose) }
		writeView(node, pose)
		if (linking) awaitLink(node, variantOf(SAMPLE), filter, SAMPLE.name, obj.id)
		return node
	} catch (e) {
		logger.error('failed to build sample mesh', e)
		return null
	}
}

/** Bind the node's picture to every draw, and to any draw a later rebuild adds. */
export const bindSampleTexture = (node, texture) => {
	node._sampleTexture = texture.source
	for (const mesh of node._draws) mesh.shader.resources.uTexture = texture.source
}

/** A camera or file fill's update. */
export const updateSampleMesh = (node, obj, init, env) => {
	if (!node.shader) return
	const pose = Object.fromEntries(SAMPLE_POSE_KEYS.map(k => [k, init(k)]))
	syncGeometry(node, nodeRefs(obj), samplePose(pose), toEnv(env), textureAspect(node))
	composeShaderMesh(node, pose)
	writeView(node, pose)
}

/** Re-place a mapped node's draws through `env` at the pose it last placed at: what a referenced shape's live pose (an automation sample, a fade tween) moves between store writes (src/comps/PixiStage/livePose.js). One key compare when nothing moved; a move re-places and never rebakes. No-op for an unmapped, foreign or dead node. */
export const followLivePose = (node, env) => {
	if (!node?.shader || !node._draws || !node._surfaced || !node._mapping?.refs) return
	// A running field blend already carries both ends' geometry; following the live outline would rebake it every frame.
	if (node._fieldMorph && !node._fieldMorph.settled) return
	const { refs, pose } = node._mapping
	syncGeometry(node, refs, pose, env, node._sample ? textureAspect(node) : undefined)
}
